"use client";

import * as React from "react";
import Link from "next/link";
import { motion } from "motion/react";
import { ArrowRight, type LucideIcon } from "lucide-react";
import { Section } from "@/components/layout/section";

interface RelatedItem {
  title: string;
  description: string;
  href: string;
  icon?: LucideIcon;
  accent?: "red" | "purple" | "cyan" | "emerald" | "amber-gold" | "denim" | "lime" | "coral";
}

interface RelatedPagesProps {
  title?: string;
  items: RelatedItem[];
}

const ACCENT_MAP: Record<NonNullable<RelatedItem["accent"]>, { text: string; bg: string; border: string }> = {
  red: { text: "text-red", bg: "from-red/10", border: "border-red/20" },
  purple: { text: "text-purple", bg: "from-purple/10", border: "border-purple/20" },
  cyan: { text: "text-cyan", bg: "from-cyan/10", border: "border-cyan/20" },
  emerald: { text: "text-emerald", bg: "from-emerald/10", border: "border-emerald/20" },
  "amber-gold": { text: "text-amber-gold", bg: "from-amber-gold/10", border: "border-amber-gold/20" },
  denim: { text: "text-denim", bg: "from-denim/10", border: "border-denim/20" },
  lime: { text: "text-lime", bg: "from-lime/10", border: "border-lime/20" },
  coral: { text: "text-coral", bg: "from-coral/10", border: "border-coral/20" },
};

export function RelatedPages({ title = "Continue Exploring", items }: RelatedPagesProps) {
  return (
    <Section id="related" ariaLabel="Related Pages" background="muted" className="py-16 lg:py-20">
      <div className="text-center mb-10">
        <h2 className="text-display-md mb-3">{title}</h2>
        <div className="w-16 h-0.5 bg-gradient-sovereign mx-auto rounded-full" />
      </div>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {items.map((it, i) => {
          const a = ACCENT_MAP[it.accent || "purple"];
          return (
            <motion.div
              key={it.href}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-30px" }}
              transition={{ duration: 0.4, delay: i * 0.06 }}
            >
              <Link href={it.href} className="block h-full group">
                <div className={`relative glass-card rounded-2xl p-5 h-full overflow-hidden bg-gradient-to-br ${a.bg} to-transparent border ${a.border} hover:scale-[1.01] transition-all`}>
                  <div aria-hidden className="absolute -top-10 -right-10 size-32 rounded-full bg-current opacity-[0.05] blur-2xl group-hover:scale-150 transition-transform duration-700" />
                  <div className="relative flex items-start gap-3">
                    {it.icon && (
                      <div className={`size-10 rounded-lg bg-gradient-sovereign flex items-center justify-center shrink-0`}>
                        <it.icon className="size-5 text-white" />
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      <h3 className={`text-heading-md mb-1.5 ${a.text}`}>{it.title}</h3>
                      <p className="text-caption text-muted-foreground leading-relaxed mb-3">{it.description}</p>
                      <div className={`inline-flex items-center gap-1.5 text-caption font-semibold ${a.text} group-hover:gap-2.5 transition-all`}>
                        Explore <ArrowRight className="size-3" />
                      </div>
                    </div>
                  </div>
                </div>
              </Link>
            </motion.div>
          );
        })}
      </div>
    </Section>
  );
}
